import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text } from 'grommet';
import { StatusWarning } from 'grommet-icons';
import uniqid from 'uniqid';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import Case from 'case';
import { useAppTheme } from '@Components/Context';
import {
  DropButton, SimpleModal, Spinning, AppButton,
} from '@Components/Control';
import { buildDateTimeLabel } from '@Helpers/util';
import { paths } from '@Components/configs';
import { initiateFetchBulkAssetStatusRequest } from '@Actions';
import { TableWrapper, renderTableHeader, NoDataPlaceholder } from '../../SharedComponents';
import { StyledStatusBadge } from '../../ListSearches/StyledListSearches';
import { StyledDropMenu } from '../../Dashboard/StyledDashboard';
import { ASSET_UPLOADS_HEADER_MAP, STATUS_COLOR_MAP, ASSET_UPLOAD_DELAY_WARNING_HOURS } from '../constants';

const StyledWarningTooltip = styled(ReactTooltip)`
  font-family: inherit;
  border-radius: 6px;
  max-width: 300px;
  line-height: 1.4;
`;

const DELAY_WARNING_MESSAGE = 'This request is taking longer than usual due to YouTube quota restrictions. We\'re actively working on it and expect it to be resolved soon. You\'ll receive an email notification once it\'s complete.';

const isRequestDelayed = (createdAt) => {
  if (!createdAt) return false;
  const hoursElapsed = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
  return hoursElapsed > ASSET_UPLOAD_DELAY_WARNING_HOURS;
};

/** API bulk-request summaries often use these keys (processing pipeline). */
const STATUS_SUMMARY_KEYS = new Set([
  'total',
  'completed',
  'failed',
  'pending',
  'processing',
  'success',
  'error',
  'uploaded',
  'cancelled',
  'canceled',
  'queued',
  'running',
  'skipped',
  'in_progress',
  'quotaheld',
]);

function normalizeSummaryKey(k) {
  return String(k || '').toLowerCase().replace(/\s+/g, '_');
}

function isStatusKeyedSummary(summary) {
  if (!summary || typeof summary !== 'object') return false;
  const keys = Object.keys(summary);
  if (keys.length === 0) return false;
  return keys.every((k) => STATUS_SUMMARY_KEYS.has(normalizeSummaryKey(k)));
}

function buildSummaryFromAssets(assets) {
  if (!Array.isArray(assets) || assets.length === 0) return null;
  const byLabel = {};
  assets.forEach((a) => {
    const label = String(a.assetLabel || a.asset_label || '').trim() || 'Unknown';
    byLabel[label] = (byLabel[label] || 0) + 1;
  });
  return Object.keys(byLabel).length > 0 ? byLabel : null;
}

/** True when counts-by-label from assets is usable (not a single Unknown bucket). */
function hasUsableAssetLabelBreakdown(fromAssets) {
  if (!fromAssets || typeof fromAssets !== 'object') return false;
  const keys = Object.keys(fromAssets);
  if (keys.length === 0) return false;
  if (keys.length === 1 && keys[0] === 'Unknown') return false;
  return true;
}

// Pipeline status block vs per-asset-label block (both when API + assets support it).
function resolveSummarySections(summary, assets) {
  const fromAssets = buildSummaryFromAssets(assets);
  const labelsOk = hasUsableAssetLabelBreakdown(fromAssets);

  let statusByPipeline = null;
  let byAssetLabel = null;

  if (summary && typeof summary === 'object' && Object.keys(summary).length > 0) {
    if (isStatusKeyedSummary(summary)) {
      statusByPipeline = { ...summary };
      if (labelsOk) {
        byAssetLabel = fromAssets;
      }
    } else if (labelsOk) {
      byAssetLabel = fromAssets;
    } else {
      byAssetLabel = { ...summary };
    }
  } else if (labelsOk) {
    byAssetLabel = fromAssets;
  }

  return { statusByPipeline, byAssetLabel };
}

function sectionsHaveContent(sections) {
  const { statusByPipeline, byAssetLabel } = sections || {};
  const a = statusByPipeline && Object.keys(statusByPipeline).length > 0;
  const b = byAssetLabel && Object.keys(byAssetLabel).length > 0;
  return a || b;
}

function pickSummaryForModal({
  summaryModalRequestId,
  activeRow,
  bulkAssetRequestStatus,
  summaryFetchLoading,
}) {
  if (!summaryModalRequestId) {
    return {
      statusByPipeline: null,
      byAssetLabel: null,
      showSpinner: false,
    };
  }

  const fetchedMatches = bulkAssetRequestStatus
    && String(bulkAssetRequestStatus.requestId) === String(summaryModalRequestId);

  const fromList = resolveSummarySections(activeRow?.summary, activeRow?.assets);
  const fromFetch = fetchedMatches
    ? resolveSummarySections(
      bulkAssetRequestStatus.summary,
      bulkAssetRequestStatus.assets,
    )
    : null;

  const statusByPipeline = fromFetch
    ? fromFetch.statusByPipeline
    : fromList.statusByPipeline;
  const byAssetLabel = fromFetch
    ? fromFetch.byAssetLabel
    : fromList.byAssetLabel;

  const showSpinner = summaryFetchLoading
    && !fetchedMatches
    && !sectionsHaveContent(fromList);

  return { statusByPipeline, byAssetLabel, showSpinner };
}

const AssetUploadRequestsTable = ({
  small,
  loading,
  requestsData,
  handlePage,
  currentPage,
  bulkAssetRequestStatus,
  summaryFetchLoading,
  fetchBulkAssetStatus,
}) => {
  const [summaryModalRequestId, setSummaryModalRequestId] = React.useState(null);
  const [openDropId, setOpenDropId] = React.useState(null);
  const requests = requestsData?.requests || [];
  const pagination = requestsData?.pagination || {};
  const totalResults = pagination?.total || 0;
  const resultsPerPage = pagination?.limit || 50;
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const useAltBg = isDarkMode ? page.background : page.altBg;

  const activeRow = React.useMemo(
    () => requests.find((r) => String(r.requestId) === String(summaryModalRequestId)),
    [requests, summaryModalRequestId],
  );

  const {
    statusByPipeline,
    byAssetLabel,
    showSpinner,
  } = pickSummaryForModal({
    summaryModalRequestId,
    activeRow,
    bulkAssetRequestStatus,
    summaryFetchLoading,
  });

  const renderSummaryEntries = (entriesObj) => (
    Object.entries(entriesObj).filter(([k]) => normalizeSummaryKey(k) !== 'quotaheld').map(([k, v]) => (
      <Box key={k} direction="row" gap="0.5rem" align="center">
        <StyledStatusBadge background={STATUS_COLOR_MAP[k]?.background || useAltBg}>
          <Text size="1rem" color={STATUS_COLOR_MAP[k]?.text || page.modalText} weight={600}>
            {Case.capital(k)}
          </Text>
        </StyledStatusBadge>
        <Text color={button.modalText}>{v}</Text>
      </Box>
    ))
  );

  const renderActionButton = (row) => (
    <Box
      align="start"
      key={uniqid()}
    >
      <DropButton
        open={openDropId === row.requestId}
        onOpen={() => setOpenDropId(row.requestId)}
        onClose={() => setOpenDropId(null)}
        withCloseDropContent={(onClose) => (
          <Box round="var(--app-radius)" pad="small" background={button.tableBg}>
            <StyledDropMenu
              plain
              label="Summary"
              onClick={() => {
                onClose();
                setSummaryModalRequestId(row.requestId);
                fetchBulkAssetStatus(row.requestId);
              }}
              textColor={page.tableText}
              hoverTextColor={button.hoverText}
              hoverIndicator={button.hoverBg}
            />
            <StyledDropMenu
              plain
              label="View details"
              href={paths.productAssetUploadDetails.replace(':id', row.requestId)}
              onClick={() => onClose()}
              textColor={page.tableText}
              hoverTextColor={button.hoverText}
              hoverIndicator={button.hoverBg}
            />
          </Box>
        )}
      >
        {(Icon) => (
          <AppButton
            dropDown
            tableButtonStyle
            isOpen={openDropId === row.requestId}
            focusHighlight={page.focusHighlight}
            color={button.tableText}
            textSize="1rem"
            fontWeight={600}
            label="Actions"
            icon={<Icon color={button.tableText} size="0.9rem" />}
          />
        )}
      </DropButton>
    </Box>
  );

  const isDelayedRequest = (row) => {
    const isIncomplete = row.status !== 'completed' && row.status !== 'failed';
    return isIncomplete && isRequestDelayed(row.createdAt);
  };

  const renderDelayWarning = (row) => {
    if (!isDelayedRequest(row)) return null;
    const tooltipId = `delay-warning-${row.requestId}`;
    return (
      <>
        <Box data-tip data-for={tooltipId} flex={false}>
          <StatusWarning color="#E5A000" size="1rem" />
        </Box>
        <StyledWarningTooltip
          id={tooltipId}
          arrowColor={page.tooltipBg || page.altBg}
          backgroundColor={page.tooltipBg || page.altBg}
          textColor={page.tooltipText || page.primaryText}
          place="right"
          effect="solid"
          delayShow={200}
        >
          {DELAY_WARNING_MESSAGE}
        </StyledWarningTooltip>
      </>
    );
  };

  const renderDataPoint = (uid, row) => {
    if (uid === 'createdAt' || uid === 'startedAt' || uid === 'completedAt') {
      return (
        <Text size="1rem" color={page.tableText}>
          {row[uid] ? buildDateTimeLabel(row[uid]) : '—'}
        </Text>
      );
    }

    if (uid === 'assetLabel') {
      return (
        <Box direction="row" align="center" gap="0.4rem">
          {renderDelayWarning(row)}
          <Text size="1rem" color={page.tableText}>
            {row[uid] || '—'}
          </Text>
        </Box>
      );
    }

    if (uid === 'actions') {
      return renderActionButton(row);
    }

    if (uid === 'status') {
      return (
        <StyledStatusBadge background={STATUS_COLOR_MAP[row.status]?.background || useAltBg}>
          <Text size="1rem" color={STATUS_COLOR_MAP[row.status]?.text || page.tableText} weight={600}>
            {Case.capital(row.status)}
          </Text>
        </StyledStatusBadge>
      );
    }

    return (
      <Text size="1rem" color={page.tableText}>
        {row[uid] || '—'}
      </Text>
    );
  };

  const renderTableContents = () => {
    if (loading || !requests.length) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={false}
          loading={loading}
          label="No asset upload requests found."
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {ASSET_UPLOADS_HEADER_MAP.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={size}
                pad={{
                  horizontal: '0.5rem',
                  top: '1rem',
                  bottom: '0.5rem',
                }}
              >
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {requests.map((row) => (
          <Box
            key={row.requestId || uniqid()}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder,
              size: '1px',
              style: 'solid',
              side: 'bottom',
            }}
            pad={{ vertical: '1rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {ASSET_UPLOADS_HEADER_MAP.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={small ? '100%' : size}
                pad={{
                  horizontal: small ? '0' : '0.5rem',
                  vertical: small ? '0.25rem' : '0',
                }}
                direction="row"
                gap="0.5rem"
              >
                {small && display && (
                  <Text size="0.875rem" color={page.tableText}>
                    {display}
                    :
                  </Text>
                )}
                {renderDataPoint(uid, row)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <TableWrapper
      small={small}
      header="Upload Requests"
      boxPad={small ? '0.5rem' : '1rem'}
      isPaged={totalResults > resultsPerPage}
      totalResults={totalResults}
      currentResults={requests.length}
      selectedResultsCount={resultsPerPage}
      currentPage={currentPage}
      handlePage={handlePage}
      hidePagination={totalResults <= resultsPerPage}
      zeroBasedPageIndex
      overrideNextPageDisabled={!pagination?.hasMore}
      tableControls={null}
    >
      {renderTableContents()}
      {summaryModalRequestId && (
        <SimpleModal
          hideLowerSection
          small={small}
          modalTitle="Summary"
          toggleModal={() => setSummaryModalRequestId(null)}
          desktopMinWidth="18rem"
        >
          <Box direction="column" gap="medium" pad={{ vertical: '0.5rem' }}>
            {(() => {
              if (showSpinner) {
                return (
                  <Box align="center" justify="center" pad="medium">
                    <Spinning size="medium" color={page.highlight} />
                  </Box>
                );
              }
              const hasPipeline = statusByPipeline && Object.keys(statusByPipeline).length > 0;
              const hasLabels = byAssetLabel && Object.keys(byAssetLabel).length > 0;
              if (!hasPipeline && !hasLabels) {
                return <Text color={button.modalText}>—</Text>;
              }
              const showBothSectionTitles = hasPipeline && hasLabels;
              return (
                <>
                  {hasPipeline && (
                    <Box direction="column" gap="small">
                      {showBothSectionTitles && (
                        <Text size="0.8rem" weight={600} color={page.secondaryText}>
                          By processing status
                        </Text>
                      )}
                      {renderSummaryEntries(statusByPipeline)}
                    </Box>
                  )}
                  {hasLabels && (
                    <Box direction="column" gap="small">
                      {showBothSectionTitles && (
                        <Text size="0.8rem" weight={600} color={page.secondaryText}>
                          By asset label
                        </Text>
                      )}
                      {renderSummaryEntries(byAssetLabel)}
                    </Box>
                  )}
                </>
              );
            })()}
          </Box>
        </SimpleModal>
      )}
    </TableWrapper>
  );
};

function mapStateToProps(state) {
  return {
    bulkAssetRequestStatus: state.contentIdAssets.bulkAssetRequestStatus,
    summaryFetchLoading: state.fetchLoader.secondaryDataLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    { fetchBulkAssetStatus: initiateFetchBulkAssetStatusRequest },
    dispatch,
  );
}

AssetUploadRequestsTable.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  requestsData: PropTypes.shape({
    requests: PropTypes.arrayOf(PropTypes.shape({
      requestId: PropTypes.string.isRequired,
      assetLabel: PropTypes.string,
      status: PropTypes.string,
      createdAt: PropTypes.string,
      startedAt: PropTypes.string,
      completedAt: PropTypes.string,
      summary: PropTypes.object,
      assets: PropTypes.arrayOf(PropTypes.object),
    })),
    pagination: PropTypes.shape({
      total: PropTypes.number,
      limit: PropTypes.number,
      offset: PropTypes.number,
      hasMore: PropTypes.bool,
    }),
  }),
  handlePage: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  bulkAssetRequestStatus: PropTypes.oneOfType([
    PropTypes.shape({
      requestId: PropTypes.string,
      summary: PropTypes.object,
      assets: PropTypes.arrayOf(PropTypes.object),
    }),
    PropTypes.oneOf([null]),
  ]),
  summaryFetchLoading: PropTypes.bool.isRequired,
  fetchBulkAssetStatus: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssetUploadRequestsTable);
